import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import {
  ChevronLeft,
  Phone,
  Printer as PrinterIcon,
  FileText,
  CheckCircle,
  AlertCircle,
  Search,
  MessageSquare,
  XCircle,
  Loader2,
  UserCheck,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { confirmPickup, getActivePickupCode } from '../lib/pickup';
import { useRepairOps } from '../lib/hooks/useRepairOps';
import { useSMS } from '../lib/hooks/useSMS';
import { getRepairStatusBadge } from '../lib/repairStatus';
import type { HistoryOutletContext } from './CustomerHistory';
import { useTranslation } from 'react-i18next';
import type { Database } from '../lib/database.types';
import { formatPhoneNumber, formatDateTime, getRepairStageDates } from '../lib/utils';
import { useBusinessProfile } from '../lib/businessProfileContext';
import QRCode from 'qrcode';

type Repair = Database['public']['Tables']['repairs']['Row'] & {
  customer: Database['public']['Tables']['customers']['Row'];
  diagnostic: Database['public']['Tables']['diagnostics']['Row'][];
  pickup_codes?: Database['public']['Tables']['pickup_codes']['Row'][];
};
type CompletionRecord = Database['public']['Tables']['completion_records']['Row'];
type RepairPhoto = Database['public']['Tables']['repair_photos']['Row'];
type RepairStatusHistory = Database['public']['Tables']['repair_status_history']['Row'];

const RepairHistoryDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { businessProfile } = useBusinessProfile();
  const [repair, setRepair] = useState<Repair | null>(null);
  const [completionRecord, setCompletionRecord] = useState<CompletionRecord | null>(null);
  const [statusHistory, setStatusHistory] = useState<RepairStatusHistory[] | null>(null);
  const [photos, setPhotos] = useState<RepairPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const historyContext = useOutletContext<HistoryOutletContext | undefined>();
  const [pickupCode, setPickupCode] = useState('');
  const [pickupError, setPickupError] = useState('');
  const [pickupLoading, setPickupLoading] = useState(false);
  const requirePickupCode = businessProfile?.sms_enabled ?? true;

  const { createDiagnostic, cancelRepair, completeRepair } = useRepairOps();
  const { sendRepairStatusSMS, sendPickupCodeSMS } = useSMS();
  const smsEnabled = businessProfile?.sms_enabled !== false;

  // Status actions (diagnose / complete / cancel / resend code)
  const [actionNotes, setActionNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [resendingCode, setResendingCode] = useState(false);
  const [codeResent, setCodeResent] = useState(false);

  // Reset per-repair form state when switching to another repair
  useEffect(() => {
    setPickupCode('');
    setPickupError('');
    setActionNotes('');
    setActionError('');
    setShowCancelConfirm(false);
    setCodeResent(false);
  }, [id]);

  /** Load the repair and its related rows; `silent` skips the full-page spinner
   *  (used to refresh after an action). */
  const loadRepair = useCallback(
    async ({ silent = false }: { silent?: boolean } = {}) => {
      if (!id) {
        setError('Invalid repair ID');
        setLoading(false);
        return;
      }
      try {
        if (!silent) setLoading(true);
        const { data: repairData, error: repairError } = await supabase
          .from('repairs')
          .select(`*, customer:customers(*), diagnostic:diagnostics(*), pickup_codes(*)`)
          .eq('id', id)
          .single();

        if (repairError || !repairData) {
          setError(repairError?.message || 'Repair not found');
          setRepair(null);
          return;
        }

        setRepair(repairData as Repair);

        const [{ data: crData }, { data: historyData }, { data: photosData }] = await Promise.all([
          supabase
            .from('completion_records')
            .select('*')
            .eq('repair_id', repairData.id)
            .maybeSingle(),
          supabase
            .from('repair_status_history')
            .select('*')
            .eq('repair_id', repairData.id)
            .order('changed_at', { ascending: true }),
          supabase
            .from('repair_photos')
            .select('*')
            .eq('repair_id', repairData.id)
            .order('created_at', { ascending: true }),
        ]);

        setCompletionRecord(crData || null);
        setStatusHistory(historyData || []);
        setPhotos(photosData || []);
      } catch (err) {
        console.error('Error fetching repair:', err);
        setError(err instanceof Error ? err.message : 'Failed to load repair');
      } finally {
        setLoading(false);
      }
    },
    [id]
  );

  useEffect(() => {
    loadRepair();
  }, [loadRepair]);

  // After any status change: reload this page and tell the history list/counts
  const afterStatusChange = async () => {
    setActionNotes('');
    setActionError('');
    setShowCancelConfirm(false);
    await loadRepair({ silent: true });
    historyContext?.onRepairStatusChanged();
  };

  // Hand a ready repair over to the customer after checking the pickup code
  const handlePickup = async () => {
    if (!repair) return;
    setPickupLoading(true);
    const errorMessage = await confirmPickup({
      repairId: repair.id,
      codes: repair.pickup_codes,
      enteredCode: pickupCode,
      requireCode: requirePickupCode,
    });
    setPickupLoading(false);

    if (errorMessage) {
      setPickupError(errorMessage);
      return;
    }
    setPickupError('');
    setPickupCode('');
    await afterStatusChange();
  };

  // Qabul qilingan -> Sozlanmoqda
  const handleDiagnose = async () => {
    if (!repair) return;
    setActionLoading(true);
    setActionError('');
    const diagnostic = await createDiagnostic({
      repair_id: repair.id,
      estimated_time: null,
      estimated_cost: null,
      required_parts: null,
      notes: actionNotes.trim() || null,
    });
    setActionLoading(false);
    if (!diagnostic) {
      setActionError("Diagnostikani saqlashda xatolik yuz berdi");
      return;
    }
    await afterStatusChange();
  };

  // Sozlanmoqda -> Tayyor: make sure a pickup code exists, mark fixed, record
  // the completion and send the "ready" SMS with the code.
  const handleComplete = async () => {
    if (!repair) return;
    setActionLoading(true);
    setActionError('');
    try {
      let code = getActivePickupCode(repair.pickup_codes);
      if (!code) {
        await supabase.rpc('generate_unique_pickup_code', { repair_id_param: repair.id });
        const { data: codes } = await supabase
          .from('pickup_codes')
          .select('*')
          .eq('repair_id', repair.id);
        code = getActivePickupCode(codes);
      }

      const { error: updateError } = await supabase
        .from('repairs')
        .update({ status: 'fixed' })
        .eq('id', repair.id);
      if (updateError) throw updateError;

      const completion = await completeRepair(repair.id, {
        notes: actionNotes.trim() || null,
        notification_sent: false,
      });
      if (!completion) throw new Error('completion record not saved');

      try {
        await sendRepairStatusSMS(
          repair.customer.phone,
          repair.customer.name,
          'ready',
          repair.repair_id,
          businessProfile?.master_name || 'Master',
          businessProfile?.phone,
          code ?? undefined
        );
      } catch (smsError) {
        console.error('Failed to send ready SMS:', smsError);
      }

      await afterStatusChange();
    } catch (err) {
      console.error('Failed to complete repair:', err);
      setActionError("Ta'mirlashni yakunlashda xatolik yuz berdi");
    } finally {
      setActionLoading(false);
    }
  };

  // Qabul qilingan / Sozlanmoqda -> Bekor qilingan (note = reason)
  const handleCancel = async () => {
    if (!repair) return;
    setActionLoading(true);
    setActionError('');
    const success = await cancelRepair(repair.id, actionNotes.trim() || null);
    setActionLoading(false);
    if (!success) {
      setActionError("Bekor qilishda xatolik yuz berdi");
      return;
    }
    await afterStatusChange();
  };

  const handleResendCode = async () => {
    if (!repair) return;
    const code = getActivePickupCode(repair.pickup_codes);
    if (!code) {
      setPickupError("Bu ta'mirlash uchun olib ketish kodi topilmadi");
      return;
    }
    setResendingCode(true);
    try {
      await sendPickupCodeSMS(
        repair.customer.phone,
        repair.customer.name,
        code,
        repair.repair_id,
        businessProfile?.business_name
      );
      setCodeResent(true);
    } catch (err) {
      console.error('Failed to resend pickup code SMS:', err);
      setPickupError('Kodni yuborishda xatolik yuz berdi');
    } finally {
      setResendingCode(false);
    }
  };

  const getNotesHistory = () => {
    if (!repair) return [];

    const history: Array<{
      stage: string;
      notes: string;
      date: string;
      icon: React.ReactNode;
    }> = [];

    if (repair.issue_description) {
      history.push({
        stage: 'Qabul qilish',
        notes: repair.issue_description,
        date: repair.created_at,
        icon: <FileText className="w-4 h-4" />,
      });
    }

    if (repair.diagnostic?.length) {
      repair.diagnostic.forEach((d) => {
        // One history row per diagnostic, with its details folded into the note
        const details = [
          d.notes,
          d.estimated_cost ? `Narx: ${d.estimated_cost.toLocaleString()} UZS` : null,
          d.estimated_time ? `Vaqt: ${d.estimated_time} soat` : null,
          d.required_parts ? `Qismlar: ${d.required_parts}` : null,
        ].filter(Boolean);
        if (details.length) {
          history.push({
            stage: 'Diagnostika',
            notes: details.join(' · '),
            date: d.created_at,
            icon: <Search className="w-4 h-4" />,
          });
        }
      });
    }

    if (completionRecord?.notes) {
      history.push({
        stage: 'Yakunlash',
        notes: completionRecord.notes,
        date: completionRecord.completed_at,
        icon: <CheckCircle className="w-4 h-4" />,
      });
    }

    if (repair.status === 'picked' || repair.status === 'completed') {
      history.push({
        stage: 'Topshirish',
        notes: '',
        date: repair.updated_at,
        icon: <CheckCircle className="w-4 h-4" />,
      });
    }

    if (repair.cancellation_note) {
      history.push({
        stage: 'Bekor qilish',
        notes: repair.cancellation_note,
        date: repair.updated_at,
        icon: <AlertCircle className="w-4 h-4" />,
      });
    }

    return history.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !repair) {
    return (
      <div className="p-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4 min-h-[44px]"
        >
          <ChevronLeft className="w-6 h-6" />
          Orqaga
        </button>
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error || 'Repair not found'}
        </div>
      </div>
    );
  }

  const stageDates = getRepairStageDates(repair, repair.diagnostic, completionRecord, statusHistory);
  const currentStatusDate =
    repair.status === 'diagnosed'
      ? stageDates.diagnosed
      : repair.status === 'fixed'
      ? stageDates.fixed
      : repair.status === 'picked'
      ? stageDates.picked
      : repair.status === 'cancelled'
      ? stageDates.cancelled
      : stageDates.accepted;

  const notesHistory = getNotesHistory();
  const statusBadge = getRepairStatusBadge(repair.status);
  // Price from the most recent diagnostic that has one
  const latestCost =
    [...(repair.diagnostic ?? [])]
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .find((d) => d.estimated_cost)?.estimated_cost ?? null;

  return (
    <div className="pb-14 md:pb-16 lg:pb-0">
      {/* Sticky panel: Back + Actions */}
      <div className="sticky top-12 md:top-14 lg:top-14 z-10 bg-white border-b border-gray-200 -mt-2 -mx-2 md:-mt-3 md:-mx-3 lg:-mt-6 lg:-mx-4 px-2 md:px-3 lg:px-4 py-2 lg:mt-0 lg:mx-0 lg:px-0 lg:border-0 lg:pb-4 flex items-center justify-between gap-2">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium min-h-[36px] text-sm shrink-0"
          aria-label="Orqaga"
        >
          <ChevronLeft className="w-5 h-5" />
          Orqaga
        </button>
        <div className="flex items-center gap-2">
        <button
          onClick={async () => {
            if (!businessProfile?.id) return;
            setGenerating(true);
            try {
              const labelUrl = `${window.location.origin}/shop/${businessProfile.id}`;
              const qrDataUrl = await QRCode.toDataURL(labelUrl, { width: 200, margin: 0 });
              const printWindow = window.open('', '_blank', 'width=800,height=600');
              if (!printWindow) return;
              const companyName = businessProfile?.master_name || 'MasterProUZ';
              printWindow.document.write(`
                <!DOCTYPE html>
                <html>
                <head>
                  <title>Print Label - ${repair.repair_id}</title>
                  <style>
                    @page { size: 70mm 30mm; margin: 0; }
                    html, body { width: 70mm; height: 30mm; margin: 0; padding: 0; background: white; }
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    body { font-family: Arial, sans-serif; }
                    .label-container { width: 70mm; height: 30mm; padding: 1mm 2mm 1.5mm 2mm; overflow: hidden; display: flex; flex-direction: column; }
                    .header { text-align: center; margin-bottom: 2mm; }
                    .company-info h3 { font-size: 4mm; font-weight: 700; text-transform: uppercase; line-height: 1.1; margin: 0; }
                    .company-info .phone { font-size: 3mm; color: #666; margin-top: 0.5mm; }
                    .main-content { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 1mm; flex: 1; }
                    .repair-info { flex: 1; min-width: 0; }
                    .ticket-id { font-size: 3.5mm; font-weight: 700; margin-bottom: 1.2mm; }
                    .info-line { font-size: 2.8mm; line-height: 1.2; margin-bottom: 0.8mm; }
                    .qr-code { width: 20mm; height: 20mm; background: #f5f5f5; border-radius: 1.5mm; display: flex; align-items: center; justify-content: center; margin-left: 2.5mm; flex-shrink: 0; }
                    .qr-code img { width: 18mm; height: 18mm; }
                    .footer { border-top: 0.3mm solid #000; padding-top: 0.5mm; margin-top: auto; }
                    .footer-text { font-size: 2mm; text-align: center; color: #000; }
                    .label-container, .main-content { page-break-inside: avoid; }
                  </style>
                </head>
                <body>
                  <div class="label-container">
                    <div class="header">
                      <div class="company-info">
                        <h3>${companyName}</h3>
                        <div class="phone">Tel: ${businessProfile?.phone || '+998 XX XXX XX XX'}</div>
                      </div>
                    </div>
                    <div class="main-content">
                      <div class="repair-info">
                        <div class="ticket-id">Tiket ID: ${repair.repair_id}</div>
                        <div class="info-line"><strong>Sana:</strong> ${new Date(repair.created_at).toLocaleDateString('uz-UZ')}</div>
                        <div class="info-line"><strong>Qurilma:</strong> ${repair.device_type}${repair.device_model ? ` - ${repair.device_model}` : ''}</div>
                      </div>
                      <div class="qr-code"><img src="${qrDataUrl}" alt="QR Code" /></div>
                    </div>
                    <div class="footer"><div class="footer-text">Yozuvning holatini kuzatish uchun skaner</div></div>
                  </div>
                </body>
                </html>
              `);
              printWindow.document.close();
              printWindow.onload = () => {
                printWindow.focus();
                printWindow.print();
                printWindow.onafterprint = () => printWindow.close();
              };
            } catch (err) {
              console.error('Error generating print:', err);
            } finally {
              setGenerating(false);
            }
          }}
          disabled={generating}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 min-h-[36px] text-sm shrink-0"
        >
          <PrinterIcon className="w-4 h-4" />
          {generating ? '...' : 'Chop etish'}
        </button>
        </div>
      </div>

      {/* One compact document card */}
      <section className="bg-white border border-gray-200 rounded-lg text-sm">
        {/* Header: number, status, dates */}
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-4 py-3 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-bold text-gray-900">
              № <span className="font-mono text-blue-600">{repair.repair_id}</span>
            </h2>
            <span className={`px-2.5 py-0.5 rounded-md text-xs font-semibold ${statusBadge.className}`}>
              {statusBadge.label}
            </span>
          </div>
          <div className="text-xs text-gray-500">
            Qabul: <span className="text-gray-800">{formatDateTime(repair.created_at)}</span>
            <span className="mx-2 text-gray-300">|</span>
            {t('currentStatusDate')}:{' '}
            <span className="text-gray-800">{formatDateTime(currentStatusDate || repair.created_at)}</span>
          </div>
        </div>

        {/* Key facts */}
        <dl className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 px-4 py-3">
          <div className="min-w-0">
            <dt className="text-xs text-gray-500">Mijoz</dt>
            <dd className="font-medium truncate">
              {repair.customer ? (
                <a
                  href={`/customer/${repair.customer.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/customer/${repair.customer.id}`);
                  }}
                  className="text-blue-600 hover:underline"
                >
                  {repair.customer.name}
                </a>
              ) : (
                '—'
              )}
            </dd>
            {repair.customer && (
              <a
                href={`tel:${repair.customer.phone}`}
                className="inline-flex items-center gap-1 text-gray-600 hover:text-blue-600"
              >
                {formatPhoneNumber(repair.customer.phone)}
                <Phone className="w-3.5 h-3.5" />
              </a>
            )}
          </div>
          <div className="min-w-0">
            <dt className="text-xs text-gray-500">Qurilma</dt>
            <dd className="font-medium text-gray-900 truncate">
              <span className="capitalize">{repair.device_type}</span>
              {repair.device_model && <span className="text-gray-600"> · {repair.device_model}</span>}
            </dd>
          </div>
          <div className="min-w-0">
            <dt className="text-xs text-gray-500">Seriya raqami</dt>
            <dd className="font-medium text-gray-900 truncate">{repair.serial_number || '—'}</dd>
          </div>
          <div className="min-w-0">
            <dt className="text-xs text-gray-500">Taxminiy narx</dt>
            <dd className="font-medium text-gray-900">
              {latestCost ? `${latestCost.toLocaleString()} UZS` : '—'}
            </dd>
          </div>
          {repair.issue_description && (
            <div className="col-span-2 lg:col-span-4 min-w-0">
              <dt className="text-xs text-gray-500">Muammo</dt>
              <dd className="text-gray-900 whitespace-pre-wrap">{repair.issue_description}</dd>
            </div>
          )}
          {repair.status === 'cancelled' && repair.cancellation_note && (
            <div className="col-span-2 lg:col-span-4 min-w-0">
              <dt className="text-xs text-gray-500">Bekor qilish sababi</dt>
              <dd className="text-red-700 whitespace-pre-wrap">{repair.cancellation_note}</dd>
            </div>
          )}
        </dl>

        {/* Photos */}
        {photos.length > 0 && (
          <div className="flex flex-wrap gap-2 px-4 pb-3">
            {photos.map((photo) => (
              <a key={photo.id} href={photo.image_url} target="_blank" rel="noreferrer">
                <img
                  src={photo.image_url}
                  alt="Qurilma rasmi"
                  className="w-20 h-16 object-cover rounded border border-gray-200 hover:border-blue-400"
                />
              </a>
            ))}
          </div>
        )}

        {/* Status actions: everything from the old Diagnostika / Yakunlash pages */}
        {(repair.status === 'accepted' || repair.status === 'diagnosed') && (
          <div
            className={`px-4 py-3 border-t space-y-2 ${
              repair.status === 'accepted' ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'
            }`}
          >
            <div
              className={`flex items-center gap-1.5 font-semibold ${
                repair.status === 'accepted' ? 'text-blue-800' : 'text-green-800'
              }`}
            >
              {repair.status === 'accepted' ? <Search className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
              {repair.status === 'accepted' ? 'Diagnostika' : "Ta'mirni yakunlash"}
            </div>
            <textarea
              value={actionNotes}
              onChange={(e) => setActionNotes(e.target.value)}
              rows={2}
              placeholder={
                repair.status === 'accepted'
                  ? 'Diagnostika natijasi (ixtiyoriy)'
                  : "Bajarilgan ishlar (ixtiyoriy)"
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {repair.status === 'diagnosed' && smsEnabled && (
              <p className="flex items-start gap-1.5 text-xs text-gray-600">
                <MessageSquare className="w-4 h-4 shrink-0 text-gray-400" />
                <span>
                  <span className="font-medium">SMS: </span>
                  Hurmatli {repair.customer?.name}, sizning qurilmangiz tayyor! 5 ish kunidan kechikmasdan olib
                  ketishingizni so'raymiz. ID: {repair.repair_id}. {businessProfile?.master_name || 'Master'}
                  {businessProfile?.phone ? ` Tel: ${businessProfile.phone}` : ''} KOD: ****
                </span>
              </p>
            )}

            {showCancelConfirm ? (
              <div className="flex flex-wrap items-center gap-2 p-2 bg-red-50 border border-red-200 rounded-md">
                <span className="flex-1 min-w-[200px] text-sm text-red-700">
                  Ta'mirlash bekor qilinadi. Yuqoridagi izoh bekor qilish sababi sifatida saqlanadi.
                </span>
                <button
                  type="button"
                  onClick={() => setShowCancelConfirm(false)}
                  disabled={actionLoading}
                  className="px-3 py-1.5 border border-gray-300 bg-white rounded-md text-sm hover:bg-gray-50"
                >
                  Yo'q
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={actionLoading}
                  className="px-3 py-1.5 bg-red-600 text-white rounded-md text-sm hover:bg-red-700 disabled:opacity-50"
                >
                  {actionLoading ? 'Bekor qilinmoqda...' : 'Ha, bekor qilish'}
                </button>
              </div>
            ) : (
              <div className="flex flex-wrap items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowCancelConfirm(true)}
                  disabled={actionLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 border border-red-300 text-red-600 bg-white rounded-md text-sm hover:bg-red-50 disabled:opacity-50"
                >
                  <XCircle className="w-4 h-4" />
                  Bekor qilish
                </button>
                <button
                  type="button"
                  onClick={repair.status === 'accepted' ? handleDiagnose : handleComplete}
                  disabled={actionLoading}
                  className={`flex items-center gap-1.5 px-4 py-1.5 text-white font-medium rounded-md text-sm disabled:opacity-50 ${
                    repair.status === 'accepted' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {repair.status === 'accepted'
                    ? actionLoading ? 'Saqlanmoqda...' : 'Diagnostikani yakunlash'
                    : actionLoading ? 'Saqlanmoqda...' : 'Tayyor deb belgilash'}
                </button>
              </div>
            )}
            {actionError && <p className="text-sm text-red-600">{actionError}</p>}
          </div>
        )}

        {/* Ready for pickup: confirm hand-over with the customer's pickup code */}
        {repair.status === 'fixed' && (
          <form
            className="flex flex-wrap items-center gap-2 px-4 py-3 bg-green-50 border-t border-green-200"
            onSubmit={(e) => {
              e.preventDefault();
              handlePickup();
            }}
          >
            <span className="flex items-center gap-1.5 font-semibold text-green-800 mr-1">
              <UserCheck className="w-4 h-4" />
              Olib ketish
            </span>
            {requirePickupCode ? (
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={4}
                value={pickupCode}
                onChange={(e) => {
                  setPickupCode(e.target.value.replace(/\D/g, ''));
                  setPickupError('');
                }}
                placeholder="Kod"
                aria-label="Olish kodi"
                className={`w-28 px-2 py-1.5 border rounded-md text-center font-mono tracking-[0.4em] focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                  pickupError ? 'border-red-500' : 'border-gray-300'
                }`}
              />
            ) : (
              <span className="text-gray-600">SMS o'chirilgan — Talon (chek) orqali</span>
            )}
            <button
              type="submit"
              disabled={pickupLoading || (requirePickupCode && pickupCode.length !== 4)}
              className="flex items-center gap-1.5 px-4 py-1.5 bg-green-600 text-white font-medium rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {pickupLoading ? 'Belgilanmoqda...' : 'Topshirish'}
            </button>
            {smsEnabled && (
              <button
                type="button"
                onClick={handleResendCode}
                disabled={resendingCode}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 bg-white rounded-md text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <MessageSquare className="w-4 h-4" />
                {resendingCode ? 'Yuborilmoqda...' : codeResent ? 'Kod yuborildi ✓' : 'Kodni qayta yuborish'}
              </button>
            )}
            {pickupError && <span className="text-red-600">{pickupError}</span>}
          </form>
        )}

        {/* History */}
        {notesHistory.length > 0 && (
          <div className="border-t border-gray-200">
            {/* Phones: stacked list (a 3-column table is too narrow) */}
            <ul className="md:hidden divide-y divide-gray-100">
              {notesHistory.map((item, index) => (
                <li key={index} className="px-4 py-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-1.5 font-medium text-gray-900">
                      <span className="text-blue-600">{item.icon}</span>
                      {item.stage}
                    </span>
                    <span className="text-xs text-gray-500 whitespace-nowrap">{formatDateTime(item.date)}</span>
                  </div>
                  {item.notes && <p className="mt-0.5 text-gray-700 whitespace-pre-wrap">{item.notes}</p>}
                </li>
              ))}
            </ul>
            <table className="hidden md:table w-full">
              <thead className="bg-gray-50 text-xs text-gray-500">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold whitespace-nowrap">Sana</th>
                  <th className="px-4 py-2 text-left font-semibold whitespace-nowrap">Bosqich</th>
                  <th className="px-4 py-2 text-left font-semibold">Izoh</th>
                </tr>
              </thead>
              <tbody>
                {notesHistory.map((item, index) => (
                  <tr key={index} className="border-t border-gray-100">
                    <td className="px-4 py-2 text-gray-600 whitespace-nowrap align-top">{formatDateTime(item.date)}</td>
                    <td className="px-4 py-2 align-top">
                      <span className="inline-flex items-center gap-1.5 font-medium text-gray-900 whitespace-nowrap">
                        <span className="text-blue-600">{item.icon}</span>
                        {item.stage}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-gray-700 align-top whitespace-pre-wrap">{item.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default RepairHistoryDetail;
